import React, { useContext, useEffect, useState } from 'react';
import { TextModal } from '@/components';
import { Message } from '@/entities';
import { ConnectedAccountContext } from '@/contexts';
import { ReadMessageForm } from '@/components/read-message-flow/read-message-form';

type ReadMessageFlowStep =  'denied' | 'unsupported_wallet' | 'decrypting' | 'cancelled' | 'read';

export interface ReadMessageFlowProps {
  message: Message | null;
  onClose: () => void;
}

export const ReadMessageFlow: React.FC<ReadMessageFlowProps> = ({ message, onClose }) => {
  const { connectedAccount } = useContext(ConnectedAccountContext);
  const [flowStep, setFlowStep] = useState<ReadMessageFlowStep>('decrypting');
  const [decryptedMessage, setDecryptedMessage] = useState<string>('');

  useEffect(() => {
    if (!connectedAccount || !message) {
      return;
    }

    const connectedPublicKey = connectedAccount.public_key;
    const { recipientPublicKey, senderPublicKey } = message;
    if (connectedPublicKey !== recipientPublicKey && connectedPublicKey !== senderPublicKey) {
      setFlowStep('denied');
      return;
    }

    const checkProvider = async () => {
      if (typeof (window as any).CasperWalletProvider !== 'function') {
        setFlowStep('unsupported_wallet');
        return;
      }

      // @ts-ignore
      const casperWallet = window.CasperWalletProvider();
      const supportedFeatures = await casperWallet.getActivePublicKeySupports();
      if (!supportedFeatures.includes('message-decryption')) {
        setFlowStep('unsupported_wallet');
        return;
      }

      const encryptedMessageBody = connectedPublicKey == recipientPublicKey
        ? message.body
        : message.sendersCopy;

      const decryptResult = await casperWallet.decryptMessage(
        encryptedMessageBody,
        connectedPublicKey
      );

      if (decryptResult.cancelled) {
        setFlowStep('cancelled');
        return;
      }

      setDecryptedMessage(decryptResult.decryptedMessage);
      setFlowStep('read');
    };

    checkProvider();
  }, [connectedAccount, message?.id]);

  return (
    <>
      {flowStep === 'denied' && (
        <TextModal text="Only the recipient or the sender can decrypt the message"/>
      )}

      {flowStep === 'unsupported_wallet' && (
        <TextModal text="Please use a wallet that supports message decryption. For example Casper Wallet 2.3.0 or later."/>
      )}

      {flowStep === 'decrypting' && (
        <TextModal text="Please proceed with instructions in your wallet"/>
      )}

      {flowStep === 'cancelled' && (
        <TextModal text="Decryption cancelled"/>
      )}

      {flowStep === 'read' && (
        <ReadMessageForm
          decryptedMessage={decryptedMessage}
          message={message}
          onClose={onClose}
        />
      )}
    </>
  );
};
